import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { clientSettings } from 'go-modules/models/common/client.settings';
import type { LicensesResponse } from '@go-modules/interfaces/licenses';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import type { NotificationSettings } from '@go-modules/interfaces/notification-settings';
import type { EmailVerificationResponse } from '@go-modules/enums/email-verification-response';
import type { User } from '@go-modules/interfaces/user';
import { CourseFolder } from '@go-modules/services/course/interfaces/course-folder.interface';
import type { Activity } from '@go-modules/interfaces/activity';
import type { AiUseType } from '@go-modules/interfaces/ai-use-type';
import type { AiMarkersEntitlement } from '@go-modules/interfaces/ai-markers-entitlement';

export interface RecentLoginHistory {
	agent: string;
	browser: string;
	browser_version: string;
	created_at: string;
	id: number;
	ip: string;
	is_mobile: boolean;
	is_pwa: boolean;
	mobile_device: string;
	platform: string;
	session_id: string;
	updated_at: string;
	user_id: number;
}

export interface BillingCredits {
	credits: Credit[];
	plan: {
		has_started?: boolean;
		has_ended?: boolean;
		has_plan: boolean;
		starts_at?: string;
		ends_at?: string;
	};
}

export interface Credit {
	name: string;
	type: string;
	max_video_mins: number;
	billing_entity_id: number;
	base_product_id: number;
	units_available: number | string;
}

export interface UserActivityDefaults {
	aiUseType: Partial<AiUseType> | null;
	activity?: Partial<Activity>;
}

@Injectable({
	providedIn: 'root'
})
export class UserService {
	private aiUser$: Observable<User> | null = null;

	constructor (private http: HttpClient) {}

	public getCurrentLicenses (): Observable<LicensesResponse>
	{
		return this.http.get<LicensesResponse>(`${clientSettings.GoReactV2API}/users/self/current-licenses`);
	}

	public getAiMarkersEntitlement (): Observable<AiMarkersEntitlement>
	{
		return this.http.get<AiMarkersEntitlement>(`${clientSettings.GoReactV2API}/users/self/ai-markers/entitlement`);
	}

	public getAiUser (): Observable<User> {
		if (this.aiUser$ == null) {
			this.aiUser$ = this.http.get<User>(`${clientSettings.GoReactV2API}/users/ai-user`).pipe(
				shareReplay(1)
			);
		}

		return this.aiUser$;
	}

	public getActivityDefaults (): Observable<UserActivityDefaults>
	{
		return this.http.get<UserActivityDefaults>(`${clientSettings.GoReactV2API}/users/self/activity/defaults`);
	}

	public updateAiMarkersDefault (ai_use_type_id: number | null): Observable<any>
	{
		return this.http.patch(`${clientSettings.GoReactV2API}/user-activity-default`, { ai_use_type_id });
	}

	public getExpiredLicenses (): Observable<LicensesResponse>
	{
		return this.http.get<LicensesResponse>(`${clientSettings.GoReactV2API}/users/self/expired-licenses`);
	}

	public removeFromGroup (userId: number, groupId: number) {
		return this.http.delete<void>(`${clientSettings.GoReactV2API}/users/${userId}/groups/${groupId}`);
	}

	public getSelf (): Observable<any> {
		return this.http.get<any>(`${clientSettings.GoReactV2API}/users/self`);
	}

	public updateSelf (user: User) {
		return this.http.put<User>(`${clientSettings.GoReactV2API}/users/self`, user);
	}

	public getLanguage () {
		return this.http.get<any>(`${clientSettings.GoReactV2API}/users/self/language`);
	}

	public getNotificationSettings (userId: number) {
		return this.http.get<NotificationSettings>(`${clientSettings.GoReactV2API}/users/${userId}/notification-settings`);
	}

	public saveNotificationSettings (userId: number, notificationSettings: NotificationSettings) {
		return this.http.put<NotificationSettings>(`${clientSettings.GoReactV2API}/users/${userId}/notification-settings/${notificationSettings.id}`, notificationSettings);
	}

	public validateResetCode (email: string, code: string): Observable<EmailVerificationResponse> {
		return this.http.post<EmailVerificationResponse>(`${clientSettings.GoReactV2API}/users/validate-reset`, {
			email,
			code
		});
	}

	public getUserGroups (
		userId: number,
		onlyLeaf: boolean = true,
		explicitAccessOnly: boolean = false
	): Observable<any> {
		return this.http.get<CourseFolder>(`${clientSettings.GoReactV2API}/users/${userId}/groups?explicitAccessOnly=${explicitAccessOnly}&onlyLeaf=${onlyLeaf}`);
	}

	public requiresTermsUpdate (): Observable<any> {
		return this.http.get<any>(`${clientSettings.GoReactV2API}/users/accept-new-terms`);
	}

	public sendFeedback (rating: number, feedback: string, invite?: string | null) {
		return this.http.post<void>(`${clientSettings.GoReactV2API}/customer-feedback`, {
			rating,
			feedback,
			invite
		});
	}

	public search (query: string): Observable<User[]> {
		return this.http.get<User[]>(`${clientSettings.GoReactV2API}/users/search?limit=100&query=${query}`);
	}

	public getBillingBalances (userId: number): Observable<BillingCredits> {
		return this.http.get<BillingCredits>(`${clientSettings.GoReactV2API}/users/${userId}/billing_credits`);
	}

	public getRecentLoginHistory (userId: number): Observable<RecentLoginHistory[]> {
		return this.http.get<RecentLoginHistory[]>(`${clientSettings.GoReactV2API}/users/${userId}/recent_login_history`);
	}

	public getLinkedUsers (userId: number): Observable<User[]> {
		return this.http.get<User[]>(`${clientSettings.GoReactV2API}/users/${userId}/linked_users`);
	}

	public getUser (userId: number): Observable<User> {
		return this.http.get<User>(`${clientSettings.GoReactV2API}/users/${userId}`);
	}
}
